import React, { useEffect, useState } from "react";
import { Box, Card, Typography, CircularProgress, Stepper, Step, StepLabel, Button, Alert } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { campaignService } from "../../../../services/campaignService";

export default function Registrando() {
  const navigate = useNavigate();
  const baseUrl = import.meta.env.BASE_URL || "/";
  const withBase = (path) => `${baseUrl}${path}`.replace(/\/{2,}/g, "/");
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const enviarCampana = async () => {
      try {
        setLoading(true);
        setError(null);

        // Recuperar datos del paso 1 desde sessionStorage
        const stepOneDataStr = sessionStorage.getItem('campaign_step1_data');
        if (!stepOneDataStr) {
          throw new Error('No se encontraron los datos del paso 1');
        }
        const stepOneData = JSON.parse(stepOneDataStr);

        // Recuperar datos de la plantilla desde sessionStorage o location.state
        const plantillaDataStr = sessionStorage.getItem('campaign_plantilla_data');
        if (!plantillaDataStr) {
          throw new Error('No se encontraron los datos de la plantilla');
        }
        const plantillaData = JSON.parse(plantillaDataStr);

        // Recuperar archivo Excel si existe y reconstruirlo desde base64
        let excelFile = null;
        if (stepOneData.upload_audiencia) {
          const excelFileBase64 = sessionStorage.getItem('campaign_excel_file');
          const excelFileName = sessionStorage.getItem('campaign_excel_file_name');
          const excelFileType = sessionStorage.getItem('campaign_excel_file_type');

          if (excelFileBase64 && excelFileName && excelFileType) {
            // Convertir base64 a Blob y luego a File
            const byteCharacters = atob(excelFileBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: excelFileType });
            excelFile = new File([blob], excelFileName, { type: excelFileType });
          }
        }

        // Obtener archivo multimedia desde location.state
        const multimediaFile = location.state?.multimediaFile || null;

        // Preparar filtros en el formato correcto
        const filtros = stepOneData.filtros.map(f => ({
          nombre: f.tipo,
          valores: f.valores || []
        }));

        // Convertir tipo_contenido a minúsculas si es necesario
        const tipoContenido = plantillaData.tipo_contenido.toLowerCase();

        // Formatear fecha_hora_envio
        let fechaHoraEnvio = null;
        if (stepOneData.sendType === 'specific_date' && stepOneData.sendDate && stepOneData.sendTime) {
          // Formato: "AAAA-MM-DD HH:MM"
          // Usar directamente la fecha del input (formato YYYY-MM-DD) para evitar problemas de zona horaria
          const [hours, minutes] = stepOneData.sendTime.split(':');
          // stepOneData.sendDate ya viene en formato YYYY-MM-DD del input type="date"
          fechaHoraEnvio = `${stepOneData.sendDate} ${hours}:${minutes}`;
        } else if (stepOneData.sendType === 'after_approval' || !stepOneData.sendType) {
          // Si es envío tras aprobación O si no se especificó (por defecto en campañas nuevas con plantilla nueva), enviamos la fecha actual.
          // Esto asegura que la campaña entre al ciclo de programación.
          console.log(`[Registrando] Tipo de envío detectado: ${stepOneData.sendType}. Asignando fecha actual para envío inmediato tras aprobación.`);
          fechaHoraEnvio = new Date().toISOString();
        }

        // Preparar datos para enviar
        const campaignData = {
          nombre_campaign: stepOneData.nombre_campaign,
          upload_audiencia: stepOneData.upload_audiencia,
          proyectoId: stepOneData.proyectoId,
          filtros: filtros,
          plantilla: {
            nombre: plantillaData.nombre,
            codigo_lenguaje: plantillaData.codigo_lenguaje,
            tipo_variable: plantillaData.tipo_variable,
            tipo_contenido: tipoContenido,
            cuerpo: plantillaData.cuerpo,
            parametros_ejemplo: plantillaData.parametros_ejemplo || []
          },
          fecha_hora_envio: fechaHoraEnvio
        };

        console.log('📤 [Registrando] Enviando campaña:', campaignData);

        // Enviar campaña al backend
        const response = await campaignService.crearCampaign(
          campaignData,
          excelFile,
          multimediaFile
        );

        console.log('✅ [Registrando] Campaña creada exitosamente:', response);
        setSuccess(true);

        // Limpiar sessionStorage después de enviar
        sessionStorage.removeItem('campaign_step1_data');
        sessionStorage.removeItem('campaign_plantilla_data');
        sessionStorage.removeItem('campaign_excel_file_name');
        sessionStorage.removeItem('campaign_multimedia_file_name');

        // Redirigir después de un breve delay
        setTimeout(() => {
          navigate(withBase('dashboard/automatizacion/campaigns'));
        }, 2000);

      } catch (err) {
        console.error('❌ [Registrando] Error al enviar campaña:', err);

        // Mensajes de error más descriptivos
        let errorMessage = 'Error al registrar la campaña. Por favor intenta nuevamente.';

        if (err.message) {
          if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || err.message.includes('Error de conexión')) {
            errorMessage = 'El servidor está procesando tu solicitud. Esto puede tardar varios segundos. Por favor verifica el estado de la campaña en la lista de campañas.';
          } else if (err.message.includes('tardó demasiado')) {
            errorMessage = 'La operación está tardando más de lo esperado. Por favor verifica el estado de la campaña en la lista de campañas.';
          } else {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    enviarCampana();
  }, [navigate, location]);

  return (
    <Box sx={{ background: "#f7f8fc", minHeight: "100vh", p: 4 }}>
      {/* Stepper */}
      <Box sx={{ mb: 4 }}>
        <Stepper
          activeStep={2}
          sx={{
            mb: 3,
            '& .MuiStepIcon-root.Mui-active': {
              color: '#2fa14e',
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: '#2fa14e',
            },
            '& .MuiStepLabel-label.Mui-active': {
              color: '#2fa14e',
              fontWeight: 'bold',
            },
            '& .MuiStepLabel-label.Mui-completed': {
              color: '#2fa14e',
              fontWeight: 'bold',
            },
          }}
        >
          <Step completed>
            <StepLabel>Datos de campaña</StepLabel>
          </Step>
          <Step completed>
            <StepLabel>Selección de contenido</StepLabel>
          </Step>
          <Step active>
            <StepLabel>Enviar campaña</StepLabel>
          </Step>
        </Stepper>
      </Box>

      {/* Contenido principal */}
      <Card
        sx={{
          p: 6,
          textAlign: "center",
          background: "#FFFFFF",
        }}
      >
        {loading && !error && (
          <>
            <CircularProgress
              size={60}
              sx={{
                color: "#2fa14e",
                mb: 3,
              }}
            />
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: 24,
                color: "#12261b",
                mb: 2,
              }}
            >
              Registrando campaña
            </Typography>
            <Typography
              sx={{
                color: "#6B7280",
                fontSize: 16,
                mb: 4,
              }}
            >
              Estamos procesando tu solicitud de aprobación. Por favor espera...
            </Typography>
          </>
        )}

        {error && (
          <>
            <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate(withBase('dashboard/automatizacion/campaigns/new/audiovisuales'))}
              sx={{
                background: "#2fa14e",
                color: "#fff",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 500,
                padding: "8px 20px",
                borderRadius: 2,
                "&:hover": {
                  background: "#2b9548",
                },
              }}
            >
              Regresar
            </Button>
          </>
        )}

        {success && (
          <>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: 24,
                color: "#12261b",
                mb: 2,
              }}
            >
              ¡Campaña registrada exitosamente!
            </Typography>
            <Typography
              sx={{
                color: "#6B7280",
                fontSize: 16,
                mb: 4,
              }}
            >
              Redirigiendo...
            </Typography>
          </>
        )}
      </Card>
    </Box>
  );
}
